import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, PictureInPicture2, Captions, CaptionsOff, FastForward } from "lucide-react";
import { api, assetUrl } from "@/lib/api";

function fmt(t) {
  if (!isFinite(t) || t < 0) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function StudioPlayer({ video, ident = null }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [phase, setPhase] = useState("poster"); // poster | ident | main
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [isFs, setIsFs] = useState(false);
  const [error, setError] = useState(false);
  const [capsOn, setCapsOn] = useState(true);
  const beatRef = useRef(null);
  const beatAcc = useRef(0);
  const posKey = `dcc_pos_${video.slug}`;
  const identSeenKey = `dcc_ident_seen`;

  const current = phase === "ident" ? ident : video;
  const sources = current
    ? [current.webm_url && { src: assetUrl(current.webm_url), type: "video/webm" },
       current.url && { src: assetUrl(current.url), type: "video/mp4" }].filter(Boolean)
    : [];

  const postEvent = (type, seconds = 0) =>
    api.post(`/public/cinema/${video.slug}/event`, { type, seconds }).catch(() => {});

  const startBeats = () => {
    clearInterval(beatRef.current);
    beatAcc.current = 0;
    beatRef.current = setInterval(() => {
      const v = videoRef.current;
      if (v && !v.paused && phase === "main") {
        beatAcc.current += 2;
        if (beatAcc.current >= 10) {
          postEvent("heartbeat", beatAcc.current);
          beatAcc.current = 0;
        }
      }
    }, 2000);
  };

  useEffect(() => () => clearInterval(beatRef.current), []);

  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const beginMain = () => {
    setPhase("main");
    setTimeout(() => {
      const v = videoRef.current;
      if (!v) return;
      const saved = parseFloat(localStorage.getItem(posKey) || "0");
      v.play().then(() => setPlaying(true)).catch(() => {});
      // resume applied on loadedmetadata below
    }, 30);
  };

  const handleBigPlay = () => {
    if (phase === "poster") {
      const seen = sessionStorage.getItem(identSeenKey);
      if (ident && !seen) {
        setPhase("ident");
        sessionStorage.setItem(identSeenKey, "1");
        setTimeout(() => videoRef.current?.play().then(() => setPlaying(true)).catch(() => {}), 30);
      } else {
        beginMain();
      }
    } else {
      toggle();
    }
  };

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    setDur(v.duration);
    if (phase === "main") {
      const saved = parseFloat(localStorage.getItem(posKey) || "0");
      if (saved > 10 && saved < v.duration - 15) v.currentTime = saved;
      postEvent("view");
      startBeats();
    }
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setTime(v.currentTime);
    if (phase === "main" && Math.floor(v.currentTime) % 5 === 0) {
      localStorage.setItem(posKey, String(v.currentTime));
    }
  };

  const onEnded = () => {
    if (phase === "ident") {
      beginMain();
      return;
    }
    setPlaying(false);
    localStorage.removeItem(posKey);
    postEvent("complete");
    if (beatAcc.current > 0) postEvent("heartbeat", beatAcc.current);
    clearInterval(beatRef.current);
  };

  const seek = (e) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = parseFloat(e.target.value);
    setTime(v.currentTime);
  };

  const changeSpeed = (e) => {
    const s = parseFloat(e.target.value);
    setSpeed(s);
    if (videoRef.current) videoRef.current.playbackRate = s;
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const toggleCaps = () => {
    const v = videoRef.current;
    setCapsOn(!capsOn);
    if (v && v.textTracks[0]) v.textTracks[0].mode = capsOn ? "hidden" : "showing";
  };

  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else wrapRef.current?.requestFullscreen().catch(() => {});
  };

  const togglePip = () => {
    const v = videoRef.current;
    if (!v || !document.pictureInPictureEnabled) return;
    if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {});
    else v.requestPictureInPicture().catch(() => {});
  };

  const onKey = (e) => {
    if (e.key === " " || e.key.toLowerCase() === "k") { e.preventDefault(); phase === "poster" ? handleBigPlay() : toggle(); }
    if (e.key.toLowerCase() === "f") toggleFs();
    if (e.key === "ArrowRight" && videoRef.current) videoRef.current.currentTime += 5;
    if (e.key === "ArrowLeft" && videoRef.current) videoRef.current.currentTime -= 5;
  };

  const isReels = video.division === "reels";
  const aspect = isReels ? "aspect-[9/16] max-h-[80vh] mx-auto" : "aspect-video";

  return (
    <div
      ref={wrapRef}
      tabIndex={0}
      onKeyDown={onKey}
      className={`relative bg-black overflow-hidden group outline-none ${aspect}`}
      data-testid="studio-player"
      aria-label={`Video player: ${video.title}`}
    >
      {phase === "poster" && !error && (
        <button onClick={handleBigPlay} data-testid="player-big-play"
          className="absolute inset-0 w-full h-full" aria-label={`Play ${video.title}`}>
          {video.poster_url && (
            <img src={assetUrl(video.poster_url)} alt="" className="w-full h-full object-cover" />
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors duration-300 hover:bg-black/20">
            <span className="w-20 h-20 rounded-full bg-crimson flex items-center justify-center transition-transform duration-300 hover:scale-110 shadow-[0_0_60px_rgba(183,31,50,0.45)]">
              <Play size={30} className="text-paper ml-1" />
            </span>
          </span>
        </button>
      )}

      {phase !== "poster" && (
        <video
          ref={videoRef}
          key={phase}
          className="w-full h-full object-contain"
          poster={phase === "main" ? assetUrl(video.poster_url || "") : undefined}
          preload="metadata"
          playsInline
          onLoadedMetadata={onLoadedMetadata}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          onError={() => setError(true)}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          data-testid="studio-video"
        >
          {sources.map((s) => <source key={s.src} src={s.src} type={s.type} />)}
          {phase === "main" && video.captions_url && (
            <track kind="captions" src={assetUrl(video.captions_url)} label="English" default />
          )}
        </video>
      )}

      {phase === "ident" && (
        <button onClick={beginMain} data-testid="skip-ident-btn"
          className="absolute bottom-20 right-5 z-10 font-ui text-[10px] uppercase tracking-[0.2em] bg-ink/80 border border-paper/25 text-paper px-4 py-2.5 rounded-full hover:border-paper/60 transition-colors">
          Skip Studio Ident <FastForward size={11} className="inline ml-1" />
        </button>
      )}

      {error && (
        <div className="absolute inset-0 bg-ink/95 flex flex-col items-center justify-center gap-4" data-testid="player-error">
          <p className="font-ui text-sm text-paper/70">This production could not be loaded.</p>
          <button onClick={() => { setError(false); setPhase("poster"); }} data-testid="player-retry-btn"
            className="font-ui text-xs uppercase tracking-[0.2em] border border-paper/25 text-paper px-6 py-3 rounded-full hover:border-paper/60 transition-colors">
            Try Again
          </button>
        </div>
      )}

      {phase !== "poster" && !error && (
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent px-4 pb-3 pt-10 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-300"
          data-testid="player-controls">
          <input
            type="range" min={0} max={dur || 0} step={0.1} value={time} onChange={seek}
            aria-label="Seek" data-testid="player-progress"
            className="w-full accent-[#B71F32] h-1.5 cursor-pointer"
          />
          <div className="flex items-center gap-3 mt-2">
            <button onClick={toggle} aria-label={playing ? "Pause" : "Play"} data-testid="player-toggle-btn"
              className="text-paper hover:text-crimsonhi transition-colors p-1">
              {playing ? <Pause size={19} /> : <Play size={19} />}
            </button>
            <span className="font-ui text-xs text-paper/70 tabular-nums" data-testid="player-time">
              {fmt(time)} / {fmt(dur)}
            </span>
            <span className="flex-1" />
            {phase === "main" && video.captions_url && (
              <button onClick={toggleCaps} aria-label="Toggle captions" data-testid="player-captions-btn"
                className="text-paper/80 hover:text-paper transition-colors p-1">
                {capsOn ? <Captions size={17} /> : <CaptionsOff size={17} />}
              </button>
            )}
            <select value={speed} onChange={changeSpeed} aria-label="Playback speed" data-testid="player-speed"
              className="bg-transparent font-ui text-xs text-paper/80 border border-paper/20 rounded px-1.5 py-1">
              {[0.75, 1, 1.25, 1.5, 2].map((s) => <option key={s} value={s} className="bg-ink">{s}×</option>)}
            </select>
            <button onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"} data-testid="player-mute-btn"
              className="text-paper/80 hover:text-paper transition-colors p-1">
              {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
            </button>
            {typeof document !== "undefined" && document.pictureInPictureEnabled && (
              <button onClick={togglePip} aria-label="Picture in picture" data-testid="player-pip-btn"
                className="text-paper/80 hover:text-paper transition-colors p-1">
                <PictureInPicture2 size={17} />
              </button>
            )}
            <button onClick={toggleFs} aria-label="Fullscreen" data-testid="player-fullscreen-btn"
              className="text-paper/80 hover:text-paper transition-colors p-1">
              <Maximize size={17} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
